import { Client } from "./client";

export enum ID_VERIFICATION_STATE {
  INITIALIZED = 'INITIALIZED',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export enum VERIFICATION_FLOW {
  INTERNAL = 'INTERNAL',
  SHUFTI = 'SHUFTI',
}

export enum DOCUMENT_STATE {
  MISSING = 'MISSING',
  PROVIDED = 'PROVIDED',
  SIMPLE_VALIDATION = 'SIMPLE_VALIDATION',
  CROSS_VALIDATION = 'CROSS_VALIDATION',
  VALIDATED = 'VALIDATED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum PROCESSOR {
  ITF_AUTO = 'ITF_AUTO',
  SHUFTI = 'SHUFTI',
  CLIENT = 'CLIENT',
  OPERATOR = 'OPERATOR',
}

// ── Visual config (icons, classes, colors) ──────────────────────────

export const ID_VERIFICATION_STATE_ICON: Record<ID_VERIFICATION_STATE, string> =
  {
    [ID_VERIFICATION_STATE.INITIALIZED]: 'play_circle_outline',
    [ID_VERIFICATION_STATE.IN_PROGRESS]: 'hourglass_empty',
    [ID_VERIFICATION_STATE.IN_REVIEW]: 'person',
    [ID_VERIFICATION_STATE.SUCCESS]: 'check_circle',
    [ID_VERIFICATION_STATE.FAIL]: 'cancel',
  };

export const ID_VERIFICATION_STATE_CLASS: Record<
  ID_VERIFICATION_STATE,
  string
> = {
  [ID_VERIFICATION_STATE.INITIALIZED]: 'pending',
  [ID_VERIFICATION_STATE.IN_PROGRESS]: 'pending',
  [ID_VERIFICATION_STATE.IN_REVIEW]: 'expired',
  [ID_VERIFICATION_STATE.SUCCESS]: 'approved',
  [ID_VERIFICATION_STATE.FAIL]: 'rejected',
};

export const PROCESSOR_COLOR: Record<string, string> = {
  [PROCESSOR.SHUFTI]: '#6a1b9a',
  [PROCESSOR.OPERATOR]: '#e65100',
  [PROCESSOR.ITF_AUTO]: '#546e7a',
  [PROCESSOR.CLIENT]: '#1565c0',
  SYSTEM: '#90a4ae',
};

export const VERIFICATION_FLOW_LABEL: Record<VERIFICATION_FLOW, string> = {
  [VERIFICATION_FLOW.INTERNAL]: 'Theia (Internal)',
  [VERIFICATION_FLOW.SHUFTI]: 'ShuftiPro (External)',
};

// ── Document slots ──────────────────────────────────────────────────


export enum IdCardImageTypes {
  ID_FRONT = 'ID_FRONT',
  ID_BACK = 'ID_BACK',
  SELFIE = 'SELFIE'
}

export type SlotKey = 'front' | 'back' | 'selfie';

export interface SlotConfig {
  apiType: IdCardImageTypes;
  docKey: 'idFrontId' | 'idBackId' | 'selfieId';
  stateKey: 'idFrontState' | 'idBackState' | 'selfieState';
  dtoKey: 'idFrontId' | 'idBackId' | 'selfieId';
  label: string;
  reasonKey?: string;
}

export const SLOT_CONFIGS: Record<SlotKey, SlotConfig> = {
  front: {
    apiType: IdCardImageTypes.ID_FRONT,
    docKey: 'idFrontId',
    stateKey: 'idFrontState',
    dtoKey: 'idFrontId',
    label: 'ЛK — лице',
  },
  back: {
    apiType: IdCardImageTypes.ID_BACK,
    docKey: 'idBackId',
    stateKey: 'idBackState',
    dtoKey: 'idBackId',
    label: 'ЛK— гръб',
  },
  selfie: {
    apiType: IdCardImageTypes.SELFIE,
    docKey: 'selfieId',
    stateKey: 'selfieState',
    dtoKey: 'selfieId',
    label: 'Селфи',
  },
};

export interface StartVerificationDialogData {
  client: Client;
  previousVerification: IdentityVerification | null;
}

export interface DocumentOption {
  id: number;
  name: string;
  state: DOCUMENT_STATE;
  stateClass: 'valid' | 'invalid' | 'pending' | 'validating';
  reasonLabel: string;
  isFromPrev: boolean;
}

export interface SlotControl {
  key: SlotKey;
  formControlName: 'frontId' | 'backId' | 'selfieId';
  label: string;
  apiType: IdCardImageTypes;
  options: DocumentOption[];
  hasOptions: boolean;
}

export const SLOT_KEYS: SlotKey[] = ['front', 'back', 'selfie'];

export interface FlowOption {
  value: VERIFICATION_FLOW;
  label: string;
  iconUrl: string;
}

export const FLOW_OPTIONS: FlowOption[] = [
  { value: VERIFICATION_FLOW.INTERNAL, label: 'Theia', iconUrl: '/assets/images/Theia.png' },
  { value: VERIFICATION_FLOW.SHUFTI, label: 'ShuftiPro', iconUrl: '/assets/images/ShuftiPro.png' },
];

// ── Document state parsing (state + optional rejection reason) ──────

export interface ParsedDocumentState {
  base: DOCUMENT_STATE;
  reason: string | null;
}

export function parseDocumentState(
  raw: string | null | undefined
): ParsedDocumentState {
  if (!raw) return { base: DOCUMENT_STATE.MISSING, reason: null };

  if (raw.startsWith('REJECTED_')) {
    return {
      base: DOCUMENT_STATE.REJECTED,
      reason: raw.substring('REJECTED_'.length),
    };
  }
  if (raw === 'REJECTED')
    return { base: DOCUMENT_STATE.REJECTED, reason: 'VALIDATION_FAILED' };

  return { base: raw as DOCUMENT_STATE, reason: null };
}

// ── Document state visual config ────────────────────────────────────

export const DOCUMENT_STATE_CLASS: Record<
  DOCUMENT_STATE,
  'valid' | 'invalid' | 'pending' | 'validating'
> = {
  [DOCUMENT_STATE.MISSING]: 'pending',
  [DOCUMENT_STATE.PROVIDED]: 'validating',
  [DOCUMENT_STATE.SIMPLE_VALIDATION]: 'validating',
  [DOCUMENT_STATE.CROSS_VALIDATION]: 'validating',
  [DOCUMENT_STATE.VALIDATED]: 'valid',
  [DOCUMENT_STATE.ACCEPTED]: 'valid',
  [DOCUMENT_STATE.REJECTED]: 'invalid',
};

export const DOCUMENT_STATE_ICON: Record<DOCUMENT_STATE, string> = {
  [DOCUMENT_STATE.MISSING]: 'image_not_supported',
  [DOCUMENT_STATE.PROVIDED]: 'hourglass_empty',
  [DOCUMENT_STATE.SIMPLE_VALIDATION]: 'hourglass_empty',
  [DOCUMENT_STATE.CROSS_VALIDATION]: 'hourglass_empty',
  [DOCUMENT_STATE.VALIDATED]: 'check_circle',
  [DOCUMENT_STATE.ACCEPTED]: 'check_circle',
  [DOCUMENT_STATE.REJECTED]: 'cancel',
};

export const IN_PROGRESS_STATES: DOCUMENT_STATE[] = [
  DOCUMENT_STATE.PROVIDED,
  DOCUMENT_STATE.SIMPLE_VALIDATION,
  DOCUMENT_STATE.CROSS_VALIDATION,
];

// ── Rejection reasons (Theia validation codes mapped to operator-facing labels) ──

export const REJECTION_REASON_LABEL: Record<string, string> = {
  LOW_QUALITY: 'Лошо качество',
  NO_CARD_DETECTED: 'Не е открита ЛК',
  NO_FACE_DETECTED: 'Не е открито лице',
  MULTIPLE_FACES_DETECTED: 'Множество лица',
  FACE_NOT_FULLY_VISIBLE: 'Частично видимо лице',
  NOT_APPLICABLE: 'Грешен тип документ',
  UNCLEAR_DOCUMENT: 'Неясен документ',
  SCREENSHOT_DETECTED: 'Screenshot',
  EDITED_DOCUMENT: 'Редактиран',
  FRAUD: 'Измама',
  OTHER: 'Друго',
  VALIDATION_FAILED: 'Неуспешна валидация',
};

export function formatDocumentReason(reason: string | null): string {
  if (!reason) return '';
  return REJECTION_REASON_LABEL[reason] || reason;
}

// ── DTO types ───────────────────────────────────────────────────────

export interface CreateVerificationDto {
  civilId: string;
  brand: string;
  flow: VERIFICATION_FLOW;
  documents?: {
    idFrontId?: number;
    idBackId?: number;
    selfieId?: number;
  };
}

export interface IdentityVerification {
  id: number;
  state: ID_VERIFICATION_STATE;
  flow: VERIFICATION_FLOW;
  startedAt: string;
  endedAt: string | null;

  civilId: string;
  brand: string;

  idFrontId: number | null;
  idFrontState: DOCUMENT_STATE | string | null;
  idBackId: number | null;
  idBackState: DOCUMENT_STATE | string | null;
  selfieId: number | null;
  selfieState: DOCUMENT_STATE | string | null;

  miReportId: number | null;
  videoFileId: string | null;
  workflowId: string | null;

  redirectionUrl: string | null;

  // Operator-only — populated when serialized with OPERATOR group
  idCardFront?: { id: number } | null;
  idCardBack?: { id: number } | null;
  selfie?: { id: number } | null;
  events?: IdentityVerificationEvent[];
}

export interface IdentityVerificationEvent {
  id: number;
  type: string;
  processor: string;
  reference: string | null;
  createdAt: string;
  data: Record<string, unknown> | null;
}

// ── Legacy aliases (remove once all callers migrated) ───────────────
// Keep these so the existing components compile during migration.

export { ID_VERIFICATION_STATE as ID_VERIFICATION_STATUS };
export { DOCUMENT_STATE as DOCUMENT_STATUS };
export { parseDocumentState as parseDocumentStatus };
